// Унифицированные типы API. Контракты зафиксированы в PRD §11 и frontend_rules.md.

/** Универсальный формат ошибки бэкенда (PRD §11.7). */
export type APIErrorCode =
  | "validation_error"
  | "unauthenticated"
  | "forbidden"
  | "not_found"
  | "conflict"
  | "business_rule_violation"
  | "account_locked"
  | "rate_limited"
  | "provider_unavailable"
  | "provider_timeout"
  | "internal_error";

export interface APIErrorBody {
  error: {
    code: APIErrorCode;
    message: string;
    details?: Record<string, unknown>;
  };
}

/** Известные коды ошибок → human-friendly hint для toast'ов (если нет message от бэка). */
export const ERROR_CODE_DEFAULT_MESSAGES: Record<APIErrorCode, string> = {
  validation_error: "Проверьте корректность введённых данных",
  unauthenticated: "Требуется вход в систему",
  forbidden: "Нет доступа к этому действию",
  not_found: "Ресурс не найден",
  conflict: "Конфликт данных",
  business_rule_violation: "Нарушено бизнес-правило",
  account_locked: "Аккаунт временно заблокирован",
  rate_limited: "Слишком много запросов, попробуйте позже",
  provider_unavailable: "Сервис временно недоступен",
  provider_timeout: "Сервис не ответил вовремя",
  internal_error: "Внутренняя ошибка сервера",
};

// ---------- Auth / Me ----------

export interface ToolDescriptor {
  slug: string;
  name: string;
  category: "graphic" | "text" | "admin";
  has_history?: boolean;
}

export interface MeResponse {
  id: string;
  email: string;
  name: string;
  is_superuser: boolean;
  /** Опционально — backend сейчас отдаёт 401 для is_active=false, но мы держим
   *  фронтовый guard как защиту в глубину (см. ProtectedRoute). */
  is_active?: boolean;
  tools: ToolDescriptor[];
}

export interface LoginPayload {
  email: string;
  password: string;
}

export interface LoginResponse {
  /** Бэкенд может возвращать как полный MeResponse, так и его подмножество. */
  id: string;
  email: string;
  is_active: boolean;
}

// ---------- Tools / Generation ----------

export interface DesignSystem {
  id: string;
  name: string;
  mood?: string;
  /** Полноразмерное изображение (используется только бэкендом в нейронке). */
  image_url: string;
  /** Превью (≤384 по большей стороне). Если backend не нашёл thumb.jpg —
   *  равно image_url (тот же файл, fallback без переходов). */
  thumbnail_url?: string;
}

export interface LogoItem {
  id: string | null;
  name: string;
  image_url: string | null;
  /** Превью (≤384 по большей стороне, прозрачность сохраняется). */
  thumbnail_url?: string | null;
}

export interface PipelineDescriptor {
  slug: string;
  label: string;
  target_size?: [number, number];
  /** Текстовое описание для UI tooltip'а. Источник истины — backend
   *  (`apps/pipelines/registry.py:Pipeline.description`). Может быть null/отсутствовать. */
  description?: string | null;
}

export interface BannerGeneratorConfig {
  design_systems: DesignSystem[];
  logos: LogoItem[];
  pipelines: PipelineDescriptor[];
}

export type GenerationStatus =
  | "pending"
  | "queued"
  | "processing"
  | "succeeded"
  | "failed"
  | "cancelled";

export interface TaskStatusResponse {
  task_id: string;
  generation_id: string;
  status: GenerationStatus;
  current_step?: string;
  elapsed_s?: number;
  /** Сколько раз бэкенд уже инициировал retry (PRD §4.6). UI рендерит «Попытка N+1/M». */
  retries_count: number;
  result: GenerationResult | null;
  error?: string;
  idempotent_replay?: boolean;
}

/** Пометка детерминированной проверки анонса. Формат задан бэкендом:
 *  проверки (мягкий коридор длины режима, ровно один абзац, отсутствие
 *  переносов строк, эмодзи, слова стоп-листа) выполняются на бэкенде и дают
 *  редактору ПОМЕТКУ рядом с текстом, а не отбраковку результата. */
export interface LintNotice {
  /** Машинный код правила (например, "length_soft_range"). */
  rule: string;
  /** Человекочитаемое сообщение редактору, на русском. */
  message: string;
  /** Фрагмент текста анонса, к которому относится пометка. */
  fragment: string;
}

export interface GenerationResult {
  result_artifact_type: "image" | "text" | "video" | "file";
  result_artifact_url: string | null;
  result_artifact_text: string | null;
  result_mime_type: string;
  thumbnail_url: string | null;
  /** Django DecimalField сериализуется как строка ("0.0123") или null. UI
   *  приводит через Number() перед арифметикой. */
  total_cost_usd: number | string | null;
  duration_s: number | null;
  /** Пометки детерминированных проверок. Есть только у «Анонсов для соцсетей»;
   *  пустой массив означает «чисто». У остальных инструментов (баннеры,
   *  обложки, корректура) бэкенд всегда кладёт этот ключ в ответ, но пустым
   *  списком (`backend/apps/generations/serializers.py:341`) — там просто
   *  нечего проверять. Опциональность (`lint?`) здесь не про эти инструменты,
   *  а защита на случай старых записей и сторонних ответов без этого поля. */
  lint?: LintNotice[];
}

export interface GenerateTaskHandle {
  task_id: string;
  generation_id: string;
  status: GenerationStatus;
  idempotent_replay?: boolean;
}

export interface GenerationListItem {
  id: string;
  tool_slug: string;
  pipeline_slug: string;
  pipeline_label: string;
  started_at: string;
  finished_at: string | null;
  status: GenerationStatus;
  result_artifact_type: GenerationResult["result_artifact_type"] | null;
  result_mime_type: string | null;
  thumbnail_url: string | null;
  result_artifact_url: string | null;
  /** Превью результата (для text — первые 200 символов; для image — null). */
  preview?: string | null;
  /** Стоимость в USD. null если ещё не подтянулась (fetch_cost задача
   *  выполняется через ~5 сек после succeeded). */
  total_cost_usd?: string | number | null;
  /** Только у корректуры v2 (`result_mime_type` — text/html или text/plain):
   *  "approved" — правки согласованы, "not_approved" — карточка показывает
   *  версию "все правки приняты" с пометкой. У остальных инструментов и
   *  старых записей — null/отсутствует (stage2_integration_spec.md §6.2). */
  review_status?: "approved" | "not_approved" | null;
}

export interface GenerationListResponse {
  count: number;
  page: number;
  per_page: number;
  results: GenerationListItem[];
}

// ---------- History detail (PRD §11.4) ----------

/** Тип поля в `params_schema` — задаёт renderer в `<ParamsRenderer>`.
 *  Источник истины — `Pipeline.params_schema` на backend (PRD §7.5). */
export type ParamsSchemaFieldType =
  | "text"
  | "markdown"
  | "reference"
  | "number"
  | "boolean"
  /** Корректура v2 — исходный HTML/текст, показывается через `ParamsRenderer`
   *  веткой `type: "html"` (handoff_implementation_2026-09-12.md §3.3 п.6). */
  | "html";

/** Имя справочника для `type=reference`. Frontend подгружает label по id
 *  из соответствующего набора в `BannerGeneratorConfig`. */
export type ParamsSchemaLookup = "design_system" | "logo" | "pipeline";

export interface ParamsSchemaField {
  label: string;
  type: ParamsSchemaFieldType;
  lookup?: ParamsSchemaLookup;
  /** Текст, который показать если значение поля = null (например «Без логотипа»). */
  nullable_label?: string;
}

export type ParamsSchema = Record<string, ParamsSchemaField>;

export interface GenerationDetailResponse {
  id: string;
  tool_slug: string;
  pipeline_slug: string;
  pipeline_label: string;
  started_at: string;
  finished_at: string | null;
  duration_s: number | null;
  result_artifact_type: GenerationResult["result_artifact_type"];
  result_artifact_url: string | null;
  result_artifact_text: string | null;
  result_mime_type: string | null;
  thumbnail_url: string | null;
  params: Record<string, unknown>;
  params_schema: ParamsSchema;
  /** Стоимость в USD (тот же контракт, что в листинге). */
  total_cost_usd?: string | number | null;
  // ---- Корректура v2 (stage2_integration_spec.md §6.3). Для всех записей,
  // кроме result_mime_type text/html и text/plain, — null/отсутствуют. ----
  /** Формат исходника, определённый ядром — "html" или "text". */
  result_format?: ProofreaderFormat | null;
  /** "approved" — согласовано редактором; "not_approved" — карточка
   *  показывает версию "все правки приняты" с пометкой "не согласовано";
   *  "unavailable" — сбой ядра при сборке итога (stage2_integration_spec.md
   *  §6.3) — сервер реально отдаёт это значение, раньше оно не входило в
   *  объявленный союз, и фронт (ArtifactDetailModal.tsx) был вынужден обходить
   *  это локальным расширением типа через `string | null | undefined`. */
  review_status?: "approved" | "not_approved" | "unavailable" | null;
  approved_at?: string | null;
  /** Безопасный итог для копирования: согласованная версия, если она есть,
   *  иначе сборка "все правки приняты". РЕШЕНИЕ §6.3: `result_artifact_text`
   *  у v2-записи равен этому полю — фронт копирует именно `result_artifact_text`
   *  (ArtifactDetailModal.tsx, ProofreaderPage.tsx), а не сырой ответ модели. */
  final_text?: string | null;
  /** "Ответ модели до правок редактора" — тот же ответ, но каркас и атрибуты
   *  взяты из исходника (сырой ответ через API не отдаётся никогда). */
  model_response_text?: string | null;
}

// ---------- Proofreader v2 — контракт правок ----------
// Источники: subagent_context/2026-09-15_proofreader_v2_impl/stage1_core_spec.md
// §6 (контракт JSON ядра) и stage2_integration_spec.md §6.5–6.6 (обвязка API).
// Фронт HTML не разбирает и не вставляет сырой строкой: дерево рендерится
// через React.createElement по белому списку тегов (запрет на небезопасную
// вставку HTML — security_rules.md:76, guard в scripts/check-guards.sh).

/** Формат исходника, который определило ядро (`detect_format`). */
export type ProofreaderFormat = "html" | "text";

/** Статус guard-проверок ответа модели. "hard_failed" в контракт никогда не
 *  попадает — для такой записи `GET diff/` не строится вовсе, ошибка отдельная
 *  (stage1_core_spec.md §6.1: «hard_failed в контракт не попадает»). */
export type ProofreaderGuardsStatus = "passed" | "soft_failed";

export interface ProofreaderGuardsInfo {
  status: ProofreaderGuardsStatus;
  /** Номер попытки (1 или 2), давшей итоговый ответ модели. */
  attempt: number;
  /** Коды SOFT-флагов (SOFT-6…9 и текстовые флаги режима "text"). */
  soft: string[];
  /** id блоков ("b63" и т.п.), которые модель переписала целиком — повод для
   *  жёлтого баннера "найдены подозрительно большие изменения, проверьте". */
  banner_blocks: string[];
}

/** Вид правки: обе стороны непусты — replace; `before` пуст — insert;
 *  `after` пуст — delete (stage1_core_spec.md §5.8). */
export type ProofreaderEditKind = "replace" | "insert" | "delete";

/** Флаги правки из белого списка `CONTRACT_EDIT_FLAGS` (stage1_core_spec.md
 *  §1.2, §5.8). Внутренние флаги ядра (`nbsp_restored`, `boundary_extended`)
 *  в контракт не попадают — на фронте их не существует. */
export type ProofreaderEditFlag =
  | "crosses_markup"
  | "whitespace_only"
  | "nbsp"
  | "rewritten"
  | "paragraph_break";

/** Заготовленные сырые куски HTML под три решения редактора плюс свой
 *  вариант — фронт только склеивает их в шаблон (§7.6), ничего не разбирая
 *  и не экранируя повторно (кроме текста своего варианта). */
export interface ProofreaderEditVariants {
  reject: string;
  accept: string;
  custom_prefix: string;
  custom_suffix: string;
}

/** Одна правка (stage1_core_spec.md §6.4). `before`/`after` — плоский текст:
 *  окно "было / стало" в поповере и сверка решений, восстановленных из
 *  localStorage (id правки = позиция + хеш содержимого, §5.8, §6.7). */
export interface ProofreaderEdit {
  id: string;
  /** Совпадает с `ProofreaderBlockNode.block`. */
  block: string;
  /** «Блок N» для сообщений редактору. */
  n: number;
  kind: ProofreaderEditKind;
  before: string;
  after: string;
  flags: ProofreaderEditFlag[];
  variants: ProofreaderEditVariants;
}

/** Белый список тегов для отображения (`DISPLAY_TAGS`, stage1_core_spec.md
 *  §1.2, §6.3). Фронт обязан проверить тег повторно при рендере — список с
 *  сервера ориентир, а не единственный барьер защиты. */
export type ProofreaderDisplayTag =
  | "p"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "ul"
  | "ol"
  | "li"
  | "blockquote"
  | "table"
  | "thead"
  | "tbody"
  | "tfoot"
  | "tr"
  | "td"
  | "th"
  | "caption"
  | "strong"
  | "b"
  | "em"
  | "i"
  | "u"
  | "s"
  | "sup"
  | "sub"
  | "a"
  | "span"
  | "br"
  | "hr"
  | "img"
  | "pre"
  | "code"
  | "div"
  | "figure"
  | "figcaption"
  | "dl"
  | "dt"
  | "dd";

/** Атрибуты из белого списка (§6.3) — все опциональны и относятся только к
 *  своим тегам. `href`, `src`, `style`, `class`, `id`, `on*` сервер не отдаёт
 *  никогда: ссылки и картинки рендерятся некликабельной заглушкой. */
export interface ProofreaderDisplayAttrs {
  /** td/th — только если значение состоит из цифр. */
  colspan?: string;
  rowspan?: string;
  /** ol. */
  start?: string;
  /** li — только цифры. */
  value?: string;
  /** a — значение `href` исходника, обрезанное до `DISPLAY_TITLE_MAX`
   *  (ссылка на экране не кликабельна, `href` не отдаётся). */
  title?: string;
  /** img — до `DISPLAY_ALT_MAX`; `src` не отдаётся, фронт рисует плашку
   *  "[изображение: {alt}]". */
  alt?: string;
}

/** Строчный элемент разметки внутри блока или правки. `children` отсутствует
 *  у void-тегов (`br`, `hr`, `img`). */
export interface ProofreaderElementNode {
  tag: ProofreaderDisplayTag;
  attrs?: ProofreaderDisplayAttrs;
  children?: ProofreaderNode[];
}

/** Единица `React.memo` — один "прогон" исходника (§2.3 ядра). `block` —
 *  id, совпадающий с `ProofreaderEdit.block`; `n` — «блок N» в сообщениях. */
export interface ProofreaderBlockNode {
  block: string;
  n: number;
  children: ProofreaderNode[];
}

/** Правка внутри дерева документа. Обе стороны — поддеревья со своей
 *  строчной разметкой: правка может пересекать границу тега (§6.2), тогда
 *  редактор видит слово целиком — красное «п̲равлах», затем зелёное «п̲равилах».
 *  `edit` совпадает с `ProofreaderEdit.id`. */
export interface ProofreaderEditNode {
  edit: string;
  before: ProofreaderNode[];
  after: ProofreaderNode[];
}

/** Узел дерева документа. Тип различается по присутствующему ключу, не по
 *  общему дискриминанту: строка — текст (React экранирует сам), иначе —
 *  `tag` (`ProofreaderElementNode`), `block` (`ProofreaderBlockNode`) или
 *  `edit` (`ProofreaderEditNode`). */
export type ProofreaderNode =
  | string
  | ProofreaderElementNode
  | ProofreaderBlockNode
  | ProofreaderEditNode;

/** Слот правки в шаблоне сборки — `{"e": edit.id}` (`SlotRef` ядра). */
export interface ProofreaderTemplateSlot {
  e: string;
}

/** Шаблон "сырые куски исходника + слоты правок", в порядке документа. Фронт
 *  строит живой предпросмотр итогового HTML только склейкой этих кусков
 *  (§7.6) — HTML не разбирается и не парсится нигде на фронте. */
export type ProofreaderTemplatePiece = string | ProofreaderTemplateSlot;

/** Контракт правок целиком — ровно то, что отдаёт `build_review()` ядра
 *  (stage1_core_spec.md §6.1). Приходит внутри `ProofreaderDiffResponse.review`. */
export interface ProofreaderReviewContract {
  format: ProofreaderFormat;
  diff_version: string;
  guards: ProofreaderGuardsInfo;
  doc: ProofreaderNode[];
  edits: ProofreaderEdit[];
  template: ProofreaderTemplatePiece[];
  /** "html" — стороны правок и свой вариант нужно экранировать перед вставкой
   *  в шаблон (`escapeHtmlText`, §7.6); "none" — текстовый режим. */
  escape: "html" | "none";
  stats: { blocks: number; edits: number };
}

/** Решение редактора по одной правке — живёт в памяти открытой страницы
 *  (ProofreaderPage.tsx, РЕШЕНИЕ ВЛАДЕЛЬЦА 2026-09-16, storage_change: больше
 *  не в localStorage) и уходит в `POST approve/` (§7.7) внутри карты
 *  `{editId: decision}`. Отсутствие записи для id правки означает "accept"
 *  (решение владельца, §7.1). НЕ путать с `ProofreaderStoredDecision` ниже —
 *  та форма плоская и живёт в истории согласований, а не в запросе. */
export type ProofreaderDecision = "accept" | "reject" | { custom: string };

/** Значение поля `decision` в сохранённой (историчной) записи согласования —
 *  плоская строка, а не объект: так `decisions_to_store` уходит в
 *  `review_decisions` на сервере (§7.7 п.5) и наружу в `review_state.decisions`
 *  (§6.5 п.5). Текст своего варианта при `"custom"` — в отдельном поле `custom`. */
export type ProofreaderStoredDecisionKind = "accept" | "reject" | "custom";

/** Один элемент "полного списка правок" на момент согласования (§7.7 п.5).
 *  Хранит не только решение, но и сами `before`/`after` — История показывает
 *  их даже после того, как `DIFF_VERSION` подрастёт и id правок из текущего
 *  `GET diff/` перестанут совпадать с сохранёнными. */
export interface ProofreaderStoredDecision {
  id: string;
  block: string;
  kind: ProofreaderEditKind;
  before: string;
  after: string;
  decision: ProofreaderStoredDecisionKind;
  /** Только при `decision === "custom"` — текст, который ввёл редактор. */
  custom?: string;
}

/** Состояние согласования записи на сервере — только для чтения; карточка
 *  Истории показывает по нему ранее сохранённые решения. `decisions` — тот же
 *  массив, что `decisions_to_store` при согласовании (§7.7 п.5), НЕ карта
 *  `{editId: decision}` из `ProofreaderApprovePayload`. */
export interface ProofreaderReviewState {
  status: "approved" | "not_approved";
  approved_at: string | null;
  /** null, если запись ещё ни разу не согласовывалась. */
  diff_version: string | null;
  decisions: ProofreaderStoredDecision[] | null;
}

/** Ответ `GET /api/me/generations/{id}/diff/` (stage2_integration_spec.md
 *  §6.5). Конверт из двух ключей отделяет контракт ядра от серверного
 *  состояния согласования. Идемпотентно, ничего не пишет. */
export interface ProofreaderDiffResponse {
  review: ProofreaderReviewContract;
  review_state: ProofreaderReviewState;
}

/** Тело `POST /api/me/generations/{id}/approve/` (§7.7,
 *  stage2_integration_spec.md §6.6). Сервер пересобирает итог сам из
 *  исходника и сырого ответа модели — HTML с фронта не принимается. */
export interface ProofreaderApprovePayload {
  diff_version: string;
  decisions: Record<string, ProofreaderDecision>;
}

/** Успешный ответ согласования (200). Любой отказ ядра
 *  (`diff_version_mismatch`, `invalid_decisions`, `hard10_failed` и прочее)
 *  сервер fail-closed переводит в 409/400/422 — стандартный `APIErrorBody`,
 *  отдельный тип под отказ не заводится. */
export interface ProofreaderApproveResponse {
  generation_id: string;
  review_status: "approved";
  approved_at: string;
  approved_text: string;
}

// ---------- Admin (PRD §11.5) ----------

export interface AdminUser {
  id: number;
  email: string;
  name: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  last_login: string | null;
  date_joined: string;
}

export interface AdminUserCreatePayload {
  email: string;
  password: string;
  name?: string;
}

export interface AdminUserUpdatePayload {
  is_active?: boolean;
  name?: string;
  new_password?: string;
}

export interface AdminUserPermissionsResponse {
  user_id: number;
  tool_slugs: string[];
  updated_at?: string;
}

export interface AdminUserPermissionsUpdatePayload {
  tool_slugs: string[];
}

export interface AdminLogItem {
  id: string;
  user_email: string;
  tool_slug: string;
  pipeline_slug: string;
  status: GenerationStatus;
  /** Django DecimalField сериализуется в JSON как строка (например "0.0123") —
   *  поэтому тип number | string | null. UI приводит через `Number(...)` или
   *  parseFloat перед .toFixed. */
  total_cost_usd: number | string | null;
  started_at: string;
  finished_at: string | null;
  retries_count: number;
  error: string | null;
}

export interface AdminLogAggregates {
  total: number;
  succeeded: number;
  /** Backend суммирует Decimal'ы — для агрегата может приходить number ИЛИ строка
   *  (зависит от пути сериализации). UI должен приводить. */
  total_cost_usd: number | string;
}

export interface AdminLogsResponse {
  count: number;
  aggregates: AdminLogAggregates;
  page: number;
  per_page: number;
  results: AdminLogItem[];
}

export interface AdminLogsFilters {
  user_email?: string;
  tool?: string;
  status?: GenerationStatus;
  /** ISO-строка с tz (например `2026-05-01T00:00:00+03:00`). Конверсия из dayjs в UI. */
  period_from?: string;
  period_to?: string;
  page?: number;
  per_page?: number;
}

export interface HistoryFilters {
  period_from?: string;
  period_to?: string;
  page?: number;
  per_page?: number;
}

// ---------- Complaints (BP007) ----------

/** Одна причина жалобы из реестра инструмента (backend `complaint_reasons.py`).
 *  `requires_comment=true` делает комментарий обязательным при выборе этой причины. */
export interface ComplaintReason {
  code: string;
  label: string;
  requires_comment: boolean;
}

/** Ответ GET /api/tools/{slug}/config/ — обобщённый.
 *  Для banner_generator содержит DS/logos/pipelines; `complaint_reasons` есть у
 *  всех slug (для инструмента без набора — пустой список). */
export interface ToolConfigResponse {
  design_systems?: DesignSystem[];
  logos?: LogoItem[];
  pipelines?: PipelineDescriptor[];
  complaint_reasons?: ComplaintReason[];
}

/** Тело POST /api/complaints/ (BP007 A6/A11). */
export interface ComplaintCreatePayload {
  generation_id: string;
  reasons: string[];
  comment: string;
}

export interface ComplaintCreateResponse {
  id: number;
  generation_id: string;
  created_at: string;
}

// ---------- Admin complaints (BP007 A8/A9) ----------

export interface AdminComplaintItem {
  id: number;
  created_at: string;
  user_email: string;
  tool_slug: string;
  pipeline_slug: string;
  pipeline_label: string;
  generation_id: string;
  generation_status: GenerationStatus;
  /** Причины как [{code,label}] — лейблы резолвятся бэкендом из реестра инструмента. */
  reasons: Array<{ code: string; label: string }>;
  comment: string;
}

export interface AdminComplaintsResponse {
  count: number;
  aggregates: { total: number };
  page: number;
  per_page: number;
  results: AdminComplaintItem[];
}

export interface AdminComplaintsFilters {
  user_email?: string;
  tool?: string;
  reason?: string;
  period_from?: string;
  period_to?: string;
  page?: number;
  per_page?: number;
}
